import $ from 'jquery';
import React from 'react';

import {redirectTo} from 'BenchlingUtil';
import {DEFAULT_DURATION} from 'nav/toasts/ToastConstants';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications, {FOREVER_DURATION} from 'notifications';
import {
  buildUsageExportRequestData,
  UsageExportOpts,
  UsageExportResult,
} from 'tenants/admin/llm-features/LLMFeaturesAdminDAO';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {getXHRHeaders} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

const USAGE_EXPORT_URL = '/1/api/metering/usage:export';

function postUsageExportTask(opts: UsageExportOpts): JQueryPromise<UsageExportResult> {
  const deferred = $.Deferred<UsageExportResult>();

  $.ajax({
    url: USAGE_EXPORT_URL,
    type: 'POST',
    contentType: 'application/json',
    data: JSON.stringify(buildUsageExportRequestData(opts)),
    dataType: 'json',
    headers: getXHRHeaders(),
  })
    .done((response: TaskRunner.OngoingTaskResponse, _statusText: string, xhr: JQueryXHR) => {
      TaskRunner.resumeTask<UsageExportResult>(response.status_url, {
        taskRequestDone: 0,
        taskMeta: TaskRunner.getTaskMeta(xhr, USAGE_EXPORT_URL),
      })
        .progress((progress: number) => {
          deferred.notify(progress);
        })
        .done((result: UsageExportResult) => {
          deferred.resolve(result);
        })
        .fail((error) => {
          deferred.reject(error);
        });
    })
    .fail((error) => {
      deferred.reject(error);
    });

  return deferred.promise();
}

/** Kick off the credit usage CSV export and drive a bottom-left tray task with a progress bar. */
export function runUsageExportWithTrayTask(opts: UsageExportOpts): JQueryPromise<UsageExportResult> {
  const trayTaskId = TrayTaskActions.createTrayTask({
    heading: (
      <ToastMessage
        body="Exporting credit usage report..."
        progress={TrayTaskConstants.INDETERMINATE_PROGRESS}
      />
    ),
    progress: TrayTaskConstants.HIDE_PROGRESS,
  });

  return postUsageExportTask(opts)
    .progress((progress: number) => {
      TrayTaskActions.updateTrayTask(trayTaskId, {
        heading: <ToastMessage body="Exporting credit usage report..." progress={progress} />,
        progress: TrayTaskConstants.HIDE_PROGRESS,
      });
    })
    .done((result: UsageExportResult) => {
      redirectTo(result.url);
      TrayTaskActions.updateTrayTask(trayTaskId, {
        progress: TrayTaskConstants.SUCCESS_PROGRESS,
        heading: <ToastMessage body="Export complete. Downloading..." progress={100} />,
        duration: DEFAULT_DURATION,
        actions: [],
      });
      if (result.truncated && result.message) {
        notifications.warn(result.message, {duration: FOREVER_DURATION});
      }
    })
    .fail((response) => {
      TrayTaskActions.updateTrayTask(trayTaskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: (
          <ToastMessage
            header="Export failed"
            body={notifications.getFriendlyError(response) ?? GENERIC_ERROR_MESSAGE}
          />
        ),
      });
    });
}
